interface FocusDateRequestDateFieldOptions {
  isPrivateEventRequest: boolean
  isInstantDateRequestSimplifiedUi: boolean
  formRoot?: HTMLElement | null
}

const isElementVisible = (element: HTMLElement) => {
  if (
    element.offsetParent === null &&
    getComputedStyle(element).position !== 'fixed'
  ) {
    return false
  }
  return element.getClientRects().length > 0
}

/**
 * Moves focus to the first date input in the active date-request branch after
 * party size commit swaps PE/DR/instant sub-forms (avoids lost Tab focus).
 */
export const focusFirstDateRequestDateField = ({
  isPrivateEventRequest,
  isInstantDateRequestSimplifiedUi,
  formRoot,
}: FocusDateRequestDateFieldOptions) => {
  const root =
    formRoot ??
    document.querySelector<HTMLElement>('[data-test-id="contact-form"]')
  if (!root) {
    return
  }

  const selectors = isInstantDateRequestSimplifiedUi
    ? [
        '#date-request-instant-date',
        '[data-test-id="date-request-instant-date"]',
        '[data-test-id="date-request-instant-booking-form"] .dp__input',
      ]
    : isPrivateEventRequest
      ? [
          '#privateRequestDate',
          '.date-request-form-private-date .dp__input',
          '.date-request-form-private-date input[type="datetime-local"]',
        ]
      : ['#dateRequestDate1', '[data-test-id="date-request-date-picker-1"]']

  for (const selector of selectors) {
    const candidate = root.querySelector<HTMLElement>(selector)
    if (candidate && isElementVisible(candidate)) {
      candidate.focus()
      return
    }
  }

  const fallback = root.querySelector<HTMLElement>(
    'input[type="datetime-local"], .dp__input'
  )
  if (fallback && isElementVisible(fallback)) {
    fallback.focus()
  }
}

export const shouldRestoreDateRequestFocusAfterTicketCommit = (
  event?: FocusEvent
) => {
  const related = event?.relatedTarget
  if (!(related instanceof HTMLElement)) {
    return false
  }

  const wasDateField =
    related.id === 'privateRequestDate' ||
    related.id?.startsWith('dateRequestDate') ||
    related.id === 'date-request-instant-date' ||
    related.dataset.testId?.startsWith('date-request-date-picker') ||
    related.classList.contains('dp__input')

  if (!wasDateField) {
    return false
  }

  return !document.contains(related)
}

export const hasLostDateRequestFormFocus = (formRoot?: HTMLElement | null) => {
  const active = document.activeElement
  if (!active || active === document.body) {
    return true
  }
  if (!(active instanceof HTMLElement) || !formRoot) {
    return false
  }
  return !formRoot.contains(active)
}
